import React from 'react';
import EditorField from './_EditorField';
import DataBindingPanel from '../DataBindingPanel';
import DataSectionPanel from './DataSectionPanel';
import { useTheme } from '../../../theme/ThemeContext';

function SectionDivider({ label }) {
  return (
    <div style={{ margin: '18px 0 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
    </div>
  );
}

function ColorPicker({ value, onChange, presets }) {
  return (
    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
      {presets.map(p => (
        <button key={p.color + p.label} type="button" title={p.label} onClick={() => onChange(p.color)}
          style={{ width: 22, height: 22, borderRadius: 4, padding: 0, flexShrink: 0, cursor: 'pointer',
            border: value === p.color ? '2px solid #334155' : '1px solid #cbd5e1',
            background: p.color === '' ? 'repeating-conic-gradient(#ddd 0% 25%, #fff 0% 50%) 0 0 / 10px 10px' : p.color,
          }} />
      ))}
      <input type="color" value={value && value !== '' ? value : '#ffffff'} onChange={e => onChange(e.target.value)}
        style={{ width: 28, height: 22, padding: 1, border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }} />
      {value && value !== '' && <span style={{ fontSize: 10, color: '#64748b', fontFamily: 'monospace' }}>{value}</span>}
      {value !== '' && <button type="button" onClick={() => onChange('')} style={{ fontSize: 10, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Reset</button>}
    </div>
  );
}

export default function DataCardsEditor({ section, dispatch, appId }) {
  const { theme } = useTheme();
  const cfg = section.section_config || {};
  const layout = section.layout_config || {};
  const update = patch => dispatch({ type: 'UPDATE_SECTION_CONFIG', clientId: section._clientId, patch });
  const updateLayout = patch => dispatch({ type: 'UPDATE_LAYOUT_CONFIG', clientId: section._clientId, patch });
  const updateBinding = patch => dispatch({ type: 'UPDATE_DATA_BINDING', clientId: section._clientId, patch });

  const primaryHex = theme.primary || '#4e2e80';
  const COLOR_PRESETS = [
    { label: 'White',   color: '#ffffff' },
    { label: 'Light',   color: '#f8fafc' },
    { label: 'Light Blue', color: '#eff6ff' },
    { label: 'Primary', color: primaryHex },
    { label: 'Dark',    color: '#1e293b'  },
  ];

  const tableSlug = section.data_binding?.table_slug;

  return (
    <div>
      <DataBindingPanel appId={appId} binding={section.data_binding} onChange={updateBinding} />

      <SectionDivider label="Card Layout" />

      <EditorField label="Columns">
        <select className="form-select form-select-sm" value={layout.columns || 3}
          onChange={e => updateLayout({ columns: parseInt(e.target.value) })}>
          <option value={1}>1 column</option>
          <option value={2}>2 columns</option>
          <option value={3}>3 columns</option>
          <option value={4}>4 columns</option>
        </select>
      </EditorField>

      <EditorField label="Card Style">
        <select className="form-select form-select-sm" value={cfg.card_style || 'elevated'}
          onChange={e => update({ card_style: e.target.value })}>
          <option value="elevated">Elevated (shadow)</option>
          <option value="outlined">Outlined</option>
          <option value="flat">Flat</option>
          <option value="glass">Glass / Frosted</option>
        </select>
      </EditorField>

      <EditorField label="Card Background">
        <ColorPicker value={cfg.card_bg_color || '#ffffff'} onChange={val => update({ card_bg_color: val })} presets={COLOR_PRESETS} />
      </EditorField>

      <EditorField label="Badge / Accent Color">
        <ColorPicker value={cfg.accent_color || primaryHex} onChange={val => update({ accent_color: val })} presets={COLOR_PRESETS} />
      </EditorField>

      <EditorField label="Image Height">
        <select className="form-select form-select-sm" value={cfg.image_height || 180}
          onChange={e => update({ image_height: parseInt(e.target.value) })}>
          <option value={100}>Small (100px)</option>
          <option value={140}>Medium (140px)</option>
          <option value={180}>Large (180px)</option>
          <option value={240}>Extra Large (240px)</option>
          <option value={0}>No Image</option>
        </select>
      </EditorField>

      <EditorField label="">
        <div className="form-check">
          <input className="form-check-input" type="checkbox" id="cardHover"
            checked={cfg.hover_lift !== false} onChange={e => update({ hover_lift: e.target.checked })} />
          <label className="form-check-label" htmlFor="cardHover" style={{ fontSize: 13 }}>Hover lift effect</label>
        </div>
      </EditorField>

      <EditorField label="">
        <div className="form-check">
          <input className="form-check-input" type="checkbox" id="showPagination"
            checked={cfg.show_pagination || false} onChange={e => update({ show_pagination: e.target.checked })} />
          <label className="form-check-label" htmlFor="showPagination" style={{ fontSize: 13 }}>Show pagination</label>
        </div>
      </EditorField>

      <DataSectionPanel cfg={cfg} updateCfg={update} tableSlug={tableSlug} />
    </div>
  );
}
